import { useEffect, useMemo, useRef, useState } from "react";
import { Button, ConfirmModal, Modal, showError, showSuccess } from "@common";
import {
  useDeleteInvisalignInvoice,
  useDeleteReportFile,
  useInsuranceEftDay,
  useInvisalignInvoiceViewUrl,
  useInvisalignInvoices,
  useReportFileViewUrl,
  useReportFiles,
  useRescanInsuranceEft,
  useUploadInvisalignInvoices,
  useUploadReportFiles,
} from "@api/hooks";
import { getApiErrorMessage } from "@utils/apiErrorUtils";
import { formatMoney } from "@utils/format";

const MAX_FILE_BYTES = 15 * 1024 * 1024;
const MAX_FILES_PER_UPLOAD = 20;

// Which backend a list talks to. Both are plain PDF storage with the same
// upload/list/delete/view shape; only "files" is read for insurance EFTs.
const FILE_KINDS = {
  files: {
    useList: useReportFiles,
    useUpload: useUploadReportFiles,
    useDelete: useDeleteReportFile,
    useViewUrl: useReportFileViewUrl,
    uploadTitle: "Upload files",
    noun: "files",
  },
  invisalign: {
    useList: useInvisalignInvoices,
    useUpload: useUploadInvisalignInvoices,
    useDelete: useDeleteInvisalignInvoice,
    useViewUrl: useInvisalignInvoiceViewUrl,
    uploadTitle: "Upload Invisalign invoices",
    noun: "Invisalign invoices",
  },
};

function formatSize(bytes) {
  const value = Number(bytes || 0);
  if (value < 1024) return `${value} B`;
  if (value < 1024 * 1024) return `${Math.round(value / 1024)} KB`;
  return `${(value / (1024 * 1024)).toFixed(1)} MB`;
}

// Open the browser's PDF viewer fitted to the container, no side panel — the
// staged previews and the stored previews should look identical.
function withViewerHints(url) {
  if (!url) return url;
  const hint = "view=Fit&toolbar=1&navpanes=0";
  return url.includes("#") ? `${url}&${hint}` : `${url}#${hint}`;
}

function PdfIcon() {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 24 24"
      fill="currentColor"
      className="h-4 w-4 shrink-0 text-palette-rose-hover"
      aria-hidden="true"
    >
      <path d="M6 2a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8.83a2 2 0 0 0-.59-1.41l-4.83-4.83A2 2 0 0 0 13.17 2H6Zm7 1.5L18.5 9H14a1 1 0 0 1-1-1V3.5Z" />
    </svg>
  );
}

const EFT_IN_PROGRESS = new Set(["pending", "scanning"]);
const READ_STATUSES = new Set(["ready", "needs_review"]);

/**
 * One caption line for one amount read from a file (insurance EFTs or cheques).
 * Only clean deposit-slip reads feed the suggestion; statements repeat money
 * already on the slip, so they say "not counted".
 */
function ReadAmountLine({ label, status, amount, counted, reviewReason, isStatement }) {
  const needsReview = status === "needs_review";
  return (
    <span
      className={`mt-1 block text-[11px] font-medium ${
        needsReview ? "text-palette-rose-badge" : "text-palette-emerald-text"
      }`}
      title={needsReview ? reviewReason : undefined}
    >
      {label}: {formatMoney(amount)}
      {needsReview ? " · check file" : ""}
      {!counted && (
        <span className="font-normal text-palette-slate-secondary">
          {isStatement ? " · statement" : ""} · not counted
        </span>
      )}
    </span>
  );
}

/**
 * Caption lines under a file card: what the file's reading found — insurance
 * EFTs and insurance cheques. Files with neither (receipts, etc.) show nothing.
 */
function EftStatusLine({ scan, stalled }) {
  if (!scan) return null;
  if (EFT_IN_PROGRESS.has(scan.status)) {
    return (
      <span className="mt-1 block text-[11px] text-palette-slate-secondary">
        {stalled ? "Still waiting to be read" : "Reading insurance payments..."}
      </span>
    );
  }
  if (scan.status === "failed") {
    return (
      <span className="mt-1 block text-[11px] text-palette-rose-badge">
        Could not read insurance payments
      </span>
    );
  }
  const isStatement = scan.document_type === "insurer_eft_statement";
  const showEft = READ_STATUSES.has(scan.status);
  const showCheque = READ_STATUSES.has(scan.cheque_status);
  if (!showEft && !showCheque) return null;
  return (
    <>
      {showEft && (
        <ReadAmountLine
          label="Insurance EFTs"
          status={scan.status}
          amount={scan.total_amount}
          counted={scan.counted}
          reviewReason={scan.review_reason}
          isStatement={isStatement}
        />
      )}
      {showCheque && (
        <ReadAmountLine
          label="Insurance cheques"
          status={scan.cheque_status}
          amount={scan.cheque_total}
          counted={scan.cheque_counted}
          reviewReason={scan.cheque_review_reason}
          isStatement={isStatement}
        />
      )}
    </>
  );
}

/**
 * Caption under an Invisalign invoice card: what was read off it, or why it is
 * not counted in the Performance Report's suggestion.
 */
function InvisalignStatusLine({ invoice }) {
  if (invoice.read_status === "unreadable") {
    return (
      <span
        className="mt-1 block text-[11px] text-palette-rose-badge"
        title={invoice.review_reason}
      >
        Could not read this invoice
      </span>
    );
  }
  const isCredit = invoice.document_type === "credit_note";
  const label = invoice.is_service_charge ? "Service charge" : isCredit ? "Credit" : "Invoice";
  return (
    <>
      {invoice.total_amount !== null && invoice.total_amount !== undefined && (
        <span
          className={`mt-1 block text-[11px] font-medium ${
            invoice.is_service_charge ? "text-palette-yellow-heading" : "text-palette-emerald-text"
          }`}
        >
          {label}: {formatMoney(invoice.total_amount)}
          {invoice.practitioner_name && (
            <span className="font-normal text-palette-slate-secondary">
              {" "}
              · {invoice.practitioner_name}
            </span>
          )}
        </span>
      )}
      {invoice.review_reason && (
        <span className="mt-1 block text-[11px] text-palette-rose-badge">
          {invoice.review_reason} Not suggested.
        </span>
      )}
    </>
  );
}

/**
 * Preview of a file already stored in GCS. The signed URL is fetched on demand
 * (and refreshed before it expires) rather than eagerly for every row.
 */
function StoredFilePreview({ fileId, kind }) {
  const { data, isLoading, isError, error } = FILE_KINDS[kind].useViewUrl(fileId);

  if (isLoading) {
    return (
      <div className="flex h-full items-center justify-center text-sm text-palette-gray-secondary">
        Loading PDF...
      </div>
    );
  }
  if (isError || !data?.url) {
    return (
      <div className="flex h-full items-center justify-center text-sm text-palette-rose-badge">
        {getApiErrorMessage(error, "Unable to load this file.")}
      </div>
    );
  }
  return (
    <iframe
      key={data.url}
      src={withViewerHints(data.url)}
      title={data.filename || "Report file"}
      className="block h-full w-full border-0"
    />
  );
}

/**
 * Staging modal: pick PDFs, look at each one, drop the wrong ones, then submit.
 *
 * Previews come from `URL.createObjectURL` on the local File, so nothing is
 * sent to the server until Upload is pressed — the whole point of the staging
 * step is that a misfiled PDF never reaches storage.
 */
function UploadFilesModal({ isOpen, onClose, clinicId, date, period, periodLabel, kind }) {
  const [staged, setStaged] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const fileInputRef = useRef(null);
  const uploadFiles = FILE_KINDS[kind].useUpload();

  // Object URLs are a leak if they outlive the staged entry, so they are
  // revoked whenever an entry goes away (removed, submitted, or modal closed).
  useEffect(() => {
    if (isOpen) return;
    setStaged((current) => {
      current.forEach((entry) => URL.revokeObjectURL(entry.previewUrl));
      return [];
    });
    setActiveId(null);
  }, [isOpen]);

  const activeEntry = useMemo(
    () => staged.find((entry) => entry.id === activeId) || null,
    [staged, activeId],
  );

  function addFiles(fileList) {
    const picked = Array.from(fileList || []);
    if (picked.length === 0) return;

    const accepted = [];
    const rejected = [];
    picked.forEach((file) => {
      const isPdf = file.type === "application/pdf" || file.name.toLowerCase().endsWith(".pdf");
      if (!isPdf) {
        rejected.push(`${file.name}: only PDF files can be uploaded`);
        return;
      }
      if (file.size > MAX_FILE_BYTES) {
        rejected.push(`${file.name}: larger than ${MAX_FILE_BYTES / (1024 * 1024)} MB`);
        return;
      }
      accepted.push(file);
    });

    setStaged((current) => {
      // Same name + size + lastModified twice in the staging list is almost
      // always a double-pick, not two real files.
      const seen = new Set(current.map((entry) => entry.key));
      const next = [...current];
      accepted.forEach((file) => {
        const key = `${file.name}:${file.size}:${file.lastModified}`;
        if (seen.has(key)) return;
        seen.add(key);
        next.push({
          id: `${key}:${Math.random().toString(36).slice(2)}`,
          key,
          file,
          previewUrl: URL.createObjectURL(file),
        });
      });
      if (next.length > MAX_FILES_PER_UPLOAD) {
        rejected.push(`Only ${MAX_FILES_PER_UPLOAD} files can be uploaded at once`);
        next.splice(MAX_FILES_PER_UPLOAD).forEach((entry) => URL.revokeObjectURL(entry.previewUrl));
      }
      return next;
    });

    if (rejected.length > 0) showError(rejected.join(" · "));
  }

  function removeStaged(id) {
    setStaged((current) => {
      const entry = current.find((item) => item.id === id);
      if (entry) URL.revokeObjectURL(entry.previewUrl);
      return current.filter((item) => item.id !== id);
    });
    setActiveId((current) => (current === id ? null : current));
  }

  async function submit() {
    if (staged.length === 0) return;
    try {
      const result = await uploadFiles.mutateAsync({
        files: staged.map((entry) => entry.file),
        clinic: clinicId,
        date,
        period,
      });
      const created = result?.created || [];
      const createdCount = created.length;
      const errors = result?.errors || [];
      if (createdCount > 0) {
        // Invisalign invoices are filed by the clinic and date printed on them,
        // so say where the ones that left this page went.
        const elsewhere = created
          .filter((item) => item.clinic !== clinicId || item.date !== date)
          .map((item) => `${item.clinic} ${item.date}`);
        showSuccess(
          `${createdCount} file${createdCount === 1 ? "" : "s"} uploaded.` +
            (elsewhere.length > 0
              ? ` Filed under their invoice clinic/date: ${[...new Set(elsewhere)].join(", ")}.`
              : ""),
        );
      }
      if (errors.length > 0) {
        showError(errors.map((item) => `${item.filename}: ${item.detail}`).join(" · "));
      }
      staged.forEach((entry) => URL.revokeObjectURL(entry.previewUrl));
      setStaged([]);
      setActiveId(null);
      if (errors.length === 0) onClose();
    } catch (err) {
      const errors = err?.response?.data?.errors;
      if (Array.isArray(errors) && errors.length > 0) {
        showError(errors.map((item) => `${item.filename}: ${item.detail}`).join(" · "));
      } else {
        showError(getApiErrorMessage(err, "Failed to upload files."));
      }
    }
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={FILE_KINDS[kind].uploadTitle}
      subtitle={`${clinicId} · ${periodLabel}`}
      size="3xl"
      footer={
        <>
          <Button
            type="button"
            variant="outline"
            onClick={onClose}
            disabled={uploadFiles.isPending}
          >
            Cancel
          </Button>
          <Button
            type="button"
            variant="primary"
            onClick={submit}
            disabled={staged.length === 0 || uploadFiles.isPending}
          >
            {uploadFiles.isPending
              ? "Uploading..."
              : `Upload ${staged.length || ""} file${staged.length === 1 ? "" : "s"}`.trim()}
          </Button>
        </>
      }
    >
      <input
        ref={fileInputRef}
        type="file"
        accept="application/pdf,.pdf"
        multiple
        className="hidden"
        onChange={(event) => {
          addFiles(event.target.files);
          event.target.value = ""; // allow re-picking the same file
        }}
      />

      {/* Naming matters for scanned slips: with no text layer, the file name is
          what tells the reader it is a deposit slip. Monthly files are not read. */}
      {period === "day" && kind === "files" && (
        <div className="mb-3 rounded-lg border border-palette-amber-border bg-palette-amber-soft px-4 py-3 text-sm text-palette-amber-text">
          <span className="font-semibold">Tip:</span> name your deposit slip{" "}
          <span className="rounded bg-palette-amber-muted px-1.5 py-0.5 font-mono font-semibold text-palette-amber-primary">
            Deposit Slip {date}.pdf
          </span>{" "}
          so its insurance EFTs and cheques are picked up automatically.
        </div>
      )}

      {kind === "invisalign" && (
        <div className="mb-3 rounded-lg border border-palette-amber-border bg-palette-amber-soft px-4 py-3 text-sm text-palette-amber-text">
          Each invoice is filed under the clinic (Ship To) and{" "}
          <span className="font-semibold">Invoice Date</span> printed on it, so you can upload a
          whole month at once.
        </div>
      )}

      <div
        className="mb-4 rounded-lg border-2 border-dashed border-palette-slate-divider px-4 py-6 text-center"
        onDragOver={(event) => event.preventDefault()}
        onDrop={(event) => {
          event.preventDefault();
          addFiles(event.dataTransfer?.files);
        }}
      >
        <p className="mb-2 text-sm text-palette-slate-secondary">
          Drop PDFs here, or pick them from your computer.
        </p>
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={() => fileInputRef.current?.click()}
        >
          Choose files
        </Button>
        <p className="mt-2 text-xs text-palette-gray-secondary">
          PDF only · up to {MAX_FILE_BYTES / (1024 * 1024)} MB each · {MAX_FILES_PER_UPLOAD} files
          at once
        </p>
      </div>

      {staged.length === 0 ? (
        <p className="text-sm text-palette-slate-secondary">No files selected yet.</p>
      ) : (
        <div className="grid grid-cols-1 gap-4 md:grid-cols-[minmax(0,18rem)_minmax(0,1fr)]">
          <ul className="flex max-h-[max(20rem,calc(90vh-21rem))] flex-col gap-2 overflow-y-auto pr-1">
            {staged.map((entry) => (
              <li
                key={entry.id}
                className={`flex items-center gap-2 rounded-md border-2 bg-white p-2 ${
                  activeId === entry.id
                    ? "border-palette-indigo-label"
                    : "border-palette-slate-divider"
                }`}
              >
                <PdfIcon />
                <button
                  type="button"
                  className="min-w-0 flex-1 text-left"
                  onClick={() => setActiveId(activeId === entry.id ? null : entry.id)}
                  title={entry.file.name}
                >
                  <span className="block truncate text-xs font-medium text-palette-slate-primary">
                    {entry.file.name}
                  </span>
                  <span className="block text-[11px] text-palette-slate-secondary">
                    {formatSize(entry.file.size)}
                  </span>
                </button>
                <Button
                  type="button"
                  variant="ghost"
                  size="xs"
                  onClick={() => removeStaged(entry.id)}
                  disabled={uploadFiles.isPending}
                >
                  Remove
                </Button>
              </li>
            ))}
          </ul>

          <div className="rounded-md border border-palette-slate-divider bg-palette-slate-soft">
            {activeEntry ? (
              <iframe
                key={activeEntry.id}
                src={withViewerHints(activeEntry.previewUrl)}
                title={activeEntry.file.name}
                className="block h-[max(20rem,calc(90vh-21rem))] w-full border-0"
              />
            ) : (
              <div className="flex h-[max(20rem,calc(90vh-21rem))] items-center justify-center px-4 text-center text-sm text-palette-slate-secondary">
                Select a file on the left to check it before uploading.
              </div>
            )}
          </div>
        </div>
      )}
    </Modal>
  );
}

/**
 * Daily Financial Reporting file attachments.
 *
 * Files are stored against a clinic-day and shown back; nothing here is fed
 * into report figures — lab costs are the lab-invoice pipeline's job, and the
 * two must not be conflated. The one reading is insurance EFTs (backend
 * apps/insurance_eft), shown per card and offered as a suggestion on the
 * Collection Breakdown Report, which the user can still override.
 *
 * With `period="month"` it lists the Monthly Reconciliation's files instead:
 * `date` is any day in the month, and the files are never read for EFTs.
 *
 * With `kind="invisalign"` it lists the day's Invisalign invoices (own table and
 * GCS folder) with the same cards; each card shows the amount and doctor read
 * off the invoice instead of the EFT lines.
 */
export default function DailyReportFiles({
  clinicId,
  date,
  period = "day",
  kind = "files",
  // Shown in modal subtitles; the month name reads better than its first day.
  periodLabel = date,
  // The parent draws the border, heading and Upload button and therefore owns
  // the modal's open state.
  isUploadOpen = false,
  onUploadOpenChange,
}) {
  const [previewFile, setPreviewFile] = useState(null);
  const [pendingDelete, setPendingDelete] = useState(null);

  const isDaily = period === "day";
  const fileKind = FILE_KINDS[kind];
  const readsEft = isDaily && kind === "files";
  const { data, isLoading, isError, error } = fileKind.useList({ clinic: clinicId, date, period });
  const deleteFile = fileKind.useDelete();
  const { data: eftData } = useInsuranceEftDay({ clinic: clinicId, date }, { enabled: readsEft });
  const rescanEft = useRescanInsuranceEft();

  const files = useMemo(() => data?.results || [], [data?.results]);
  // A disabled query still returns cached data, and Invisalign ids would collide
  // with report-file ids — so only the Files list reads the scans.
  const eftScanByFileId = useMemo(
    () =>
      new Map(readsEft ? (eftData?.scans || []).map((scan) => [scan.report_file_id, scan]) : []),
    [eftData?.scans, readsEft],
  );
  // Matches the point where useInsuranceEftDay stops polling.
  const eftStalled = readsEft && (eftData?.oldest_pending_seconds ?? 0) >= 300;

  async function rescan(file) {
    try {
      await rescanEft.mutateAsync({ fileId: file.id, clinic: clinicId, date });
      showSuccess(`Reading "${file.original_filename}" again.`);
    } catch (err) {
      showError(getApiErrorMessage(err, "Failed to read the file again."));
    }
  }

  async function confirmDelete() {
    if (!pendingDelete) return;
    try {
      await deleteFile.mutateAsync({ fileId: pendingDelete.id, clinic: clinicId, date });
      if (previewFile?.id === pendingDelete.id) setPreviewFile(null);
      setPendingDelete(null);
      showSuccess("File deleted.");
    } catch (err) {
      showError(getApiErrorMessage(err, "Failed to delete the file."));
    }
  }

  return (
    <>
      {isLoading && files.length === 0 && (
        <div className="text-sm text-palette-slate-secondary">Loading...</div>
      )}

      {isError && (
        <div className="text-sm text-palette-rose-badge">
          {getApiErrorMessage(error, "Unable to load files.")}
        </div>
      )}

      {!isLoading && !isError && files.length === 0 && (
        <div className="text-sm text-palette-slate-secondary">
          No {fileKind.noun} uploaded for this {isDaily ? "date" : "month"}.
        </div>
      )}

      {files.length > 0 && (
        <div className="flex items-stretch gap-2 overflow-x-auto px-0.5 py-1">
          {files.map((file) => (
            <div
              key={file.id}
              className={`flex w-56 shrink-0 flex-col rounded-md border-2 bg-white shadow-sm transition hover:shadow-md ${
                previewFile?.id === file.id
                  ? "border-palette-indigo-label"
                  : "border-palette-slate-divider"
              }`}
            >
              <button
                type="button"
                className="flex-1 p-2 text-left"
                onClick={() => setPreviewFile(previewFile?.id === file.id ? null : file)}
                title={file.original_filename}
              >
                <span className="flex items-center gap-1.5">
                  <PdfIcon />
                  <span className="truncate text-xs font-medium text-palette-slate-primary">
                    {file.original_filename}
                  </span>
                </span>
                <span className="mt-1 block text-[11px] text-palette-slate-secondary">
                  {formatSize(file.size)}
                  {file.uploaded_by_name ? ` · ${file.uploaded_by_name}` : ""}
                </span>
                {kind === "invisalign" ? (
                  <InvisalignStatusLine invoice={file} />
                ) : (
                  <EftStatusLine scan={eftScanByFileId.get(file.id)} stalled={eftStalled} />
                )}
              </button>
              {/* `variant="link"` forces Button's own "link" size, which sets no
                  font-size — hence the explicit text-[11px] so these read as
                  captions on the card rather than body copy. */}
              <div className="flex items-center justify-between border-t border-palette-slate-divider px-2 py-1">
                <Button
                  type="button"
                  variant="link"
                  className="text-[11px] font-medium"
                  onClick={() => setPreviewFile(file)}
                >
                  View
                </Button>
                {readsEft &&
                  (eftStalled || !EFT_IN_PROGRESS.has(eftScanByFileId.get(file.id)?.status)) && (
                    <Button
                      type="button"
                      variant="link"
                      className="text-[11px] font-medium"
                      onClick={() => rescan(file)}
                      disabled={rescanEft.isPending}
                      title="Read this file for insurance EFTs and cheques"
                    >
                      {/* Receipts etc. were skipped without a read, so "again" would mislead. */}
                      {["ready", "needs_review", "failed"].includes(
                        eftScanByFileId.get(file.id)?.status,
                      ) || READ_STATUSES.has(eftScanByFileId.get(file.id)?.cheque_status)
                        ? "Rescan"
                        : "Read payments"}
                    </Button>
                  )}
                {file.can_delete && (
                  <Button
                    type="button"
                    variant="link"
                    className="text-[11px] font-medium text-palette-rose-badge hover:text-palette-red-heading"
                    onClick={() => setPendingDelete(file)}
                  >
                    Delete
                  </Button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      <UploadFilesModal
        isOpen={isUploadOpen}
        onClose={() => onUploadOpenChange?.(false)}
        clinicId={clinicId}
        date={date}
        period={period}
        periodLabel={periodLabel}
        kind={kind}
      />

      <Modal
        isOpen={Boolean(previewFile)}
        onClose={() => setPreviewFile(null)}
        title={previewFile?.original_filename || "File"}
        subtitle={`${clinicId} · ${periodLabel}`}
        size="3xl"
      >
        <div className="h-[70vh]">
          {previewFile && <StoredFilePreview fileId={previewFile.id} kind={kind} />}
        </div>
      </Modal>

      <ConfirmModal
        isOpen={Boolean(pendingDelete)}
        onClose={() => setPendingDelete(null)}
        onConfirm={confirmDelete}
        title="Delete file"
        message={`Delete "${pendingDelete?.original_filename || ""}"? This removes it from storage and cannot be undone.`}
        confirmText="Delete"
        confirmVariant="danger"
        isLoading={deleteFile.isPending}
      />
    </>
  );
}
